/**
 * Export Calculation Section Component
 *
 * @description Full section for CSV export with informational alert
 * @version 2.0.0
 * @updated 2025-10-28 - Refactored to use useCSVExport hook
 * @created 2025-10-27
 */

import {
  Paper,
  Stack,
  Text,
  Button,
  Alert,
  Group,
} from '@mantine/core';
import {
  IconFileDownload,
  IconInfoCircle,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useCSVExport } from '../../../../hooks/useCSVExport';

/**
 * Calculation Data Input
 */
interface CalculationData {
  year: number;
  monthlyConsumption: number[];
  hourlyConfig: {
    weekday: number[];
    weekend: number[];
  };
}

/**
 * Component Props
 */
interface ExportCalculationSectionProps {
  /** Calculation data for CSV generation */
  calculationData: CalculationData;
}

/**
 * Export Calculation Section Component
 *
 * Provides a dedicated section for exporting calculation data to CSV.
 * Includes informational alert explaining CSV contents and a primary export button.
 *
 * Features:
 * - Informational alert about CSV contents
 * - Loading state during generation
 * - Success/error notifications
 * - GA4 tracking
 * - Mobile-responsive design
 */
export function ExportCalculationSection({
  calculationData,
}: ExportCalculationSectionProps) {
  // Use custom hook for CSV export logic
  const { loading, handleExport } = useCSVExport({
    calculationData,
    location: 'calculator',
  });

  return (
    <Paper
      p="xl"
      shadow="sm"
      withBorder
      style={{
        borderColor: 'var(--mantine-color-green-3)',
        backgroundColor: 'var(--mantine-color-green-0)',
      }}
    >
      <Stack gap="md">
        {/* Header with Icon */}
        <Group gap="sm">
          <IconFileDownload size={28} color="var(--mantine-color-green-6)" />
          <Text size="xl" fw={600} c="green.8">
            Pobierz szczegółowe dane kalkulacji
          </Text>
        </Group>

        {/* Description */}
        <Text size="sm" c="dimmed">
          Eksportuj godzinowe dane zużycia i kosztów dla wszystkich taryf w formacie CSV
        </Text>

        {/* Info Alert */}
        <Alert
          icon={<IconInfoCircle size={20} />}
          color="blue"
          variant="light"
          title="Zawartość pliku CSV"
        >
          <Stack gap="xs">
            <Text size="sm">
              Plik CSV zawiera szczegółowe dane dla każdej godziny roku:
            </Text>
            <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
              <li>
                <Text size="sm">Data, godzina, typ dnia, święta</Text>
              </li>
              <li>
                <Text size="sm">Zużycie energii (kWh)</Text>
              </li>
              <li>
                <Text size="sm">
                  Koszty dla każdej taryfy (energia + dystrybucja)
                </Text>
              </li>
            </ul>
            <Text size="xs" c="dimmed" mt="xs">
              Format: 8760 wierszy (365 dni × 24 godziny)
            </Text>
          </Stack>
        </Alert>

        {/* Security Warning */}
        <Alert
          icon={<IconAlertTriangle size={20} />}
          color="orange"
          variant="light"
          title="Bezpieczeństwo danych"
        >
          <Text size="sm">
            Pobrany plik CSV zawiera Twoje dane osobowe i szczegółowe informacje o zużyciu energii.
            Przechowuj plik w bezpiecznym miejscu i nie udostępniaj go osobom trzecim.
            Jesteś odpowiedzialny/a za zabezpieczenie pobranego pliku.
          </Text>
        </Alert>

        {/* Export Button */}
        <Button
          leftSection={<IconFileDownload size={20} />}
          onClick={handleExport}
          loading={loading}
          disabled={loading}
          variant="filled"
          color="green"
          size="md"
          style={{ width: '100%', maxWidth: '300px' }}
        >
          Pobierz dane CSV
        </Button>

        {/* Additional info */}
        <Text size="xs" c="dimmed">
          Plik CSV można otworzyć w programie Excel, Google Sheets lub innej
          aplikacji do arkuszy kalkulacyjnych.
        </Text>
      </Stack>
    </Paper>
  );
}
